/**
 * Native (same-origin) display views for Collaborate mode. Render the live
 * activity + aggregated results directly in the Zoom app instead of iframing
 * the web display page. Dark presentation theme for the shared main stage.
 *
 * Visual parity adapted from apps/web display components (PollDisplay,
 * NpsDisplay, WordCloudDisplay, QuestionDisplay).
 */

import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { SessionNode } from '../lib/api';
import { nodeTitle } from '../lib/api';
import { buildJoinUrl } from '../lib/zoomActions';
import { fetchUploadObjectUrl, fetchSlideImageObjectUrl } from '../lib/participantApi';
import type {
  PollResults,
  NpsResults,
  WordCloudResults,
  QuestionResponse,
  QuestionSummary,
  QuizResults,
  SwotResults,
  SwotSummary,
  CrossTabResults,
  ImageActivityResults,
} from '../lib/participantApi';

/* ------------------------------------------------------------------ */
/*  Shared chrome                                                      */
/* ------------------------------------------------------------------ */

function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-10 py-12 text-white overflow-hidden">
      {children}
    </div>
  );
}

function Eyebrow({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span
      className="text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4"
      style={{ backgroundColor: color, color: '#0B1F26' }}
    >
      {children}
    </span>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="text-3xl md:text-4xl font-bold text-center leading-tight max-w-4xl">
      {children}
    </h1>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-400 text-sm mt-3 mb-8">{children}</p>;
}

/* ------------------------------------------------------------------ */
/*  Timer bar — synced countdown to the active activity's server expiry */
/* ------------------------------------------------------------------ */

export function TimerBar({
  expiresAt,
  durationMinutes,
}: {
  expiresAt: string;
  durationMinutes: number;
}) {
  const remainingFrom = () =>
    Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
  const [remaining, setRemaining] = useState(remainingFrom);

  useEffect(() => {
    setRemaining(remainingFrom());
    const t = setInterval(() => setRemaining(remainingFrom()), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  const total = durationMinutes * 60;
  const pct = total > 0 ? (remaining / total) * 100 : 0;
  const low = remaining <= 10;
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;

  return (
    <div className="px-6 py-2 bg-gray-800/95 border-b border-white/10">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-gray-400 uppercase tracking-wider">Time remaining</span>
        <span className={`text-sm font-mono font-semibold ${low ? 'text-red-400' : 'text-white'}`}>
          {mm}:{String(ss).padStart(2, '0')}
        </span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${low ? 'bg-red-500' : 'bg-primary-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Idle / waiting-to-join                                             */
/* ------------------------------------------------------------------ */

export function IdleDisplay({ sessionId }: { sessionId: string }) {
  const joinUrl = buildJoinUrl(sessionId);
  return (
    <Stage>
      <Eyebrow color="#2A7A8A">Facilitator Tools</Eyebrow>
      <Heading>Waiting for the facilitator to start an activity</Heading>
      <Sub>Scan to join on your device</Sub>
      <div className="bg-white p-4 rounded-2xl">
        <QRCodeSVG value={joinUrl} size={180} />
      </div>
      <p className="text-gray-500 text-xs mt-4 break-all max-w-md text-center">{joinUrl}</p>
    </Stage>
  );
}

/* ------------------------------------------------------------------ */
/*  Waiting (results still loading for a supported type) — NO QR, so it */
/*  doesn't flash the join screen between activities.                  */
/* ------------------------------------------------------------------ */

export function WaitingView({ node, message }: { node: SessionNode; message: string }) {
  return (
    <Stage>
      <Eyebrow color="#2A7A8A">{node.type}</Eyebrow>
      <Heading>{nodeTitle(node)}</Heading>
      <Sub>{message}</Sub>
      <div className="mt-2 h-8 w-8 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
    </Stage>
  );
}

/* ------------------------------------------------------------------ */
/*  Generic (types without a native results view yet) — no QR; people  */
/*  respond in their own app instance, not by re-scanning mid-session. */
/* ------------------------------------------------------------------ */

export function GenericActivityView({
  node,
  subtitle,
}: {
  node: SessionNode;
  subtitle: string;
}) {
  return (
    <Stage>
      <Eyebrow color="#E8A838">{node.type}</Eyebrow>
      <Heading>{nodeTitle(node)}</Heading>
      <Sub>{subtitle}</Sub>
      <p className="text-gray-400 text-sm">Respond in the Facilitator Tools app</p>
    </Stage>
  );
}

/* ------------------------------------------------------------------ */
/*  Poll                                                               */
/* ------------------------------------------------------------------ */

export function PollResultsView({ results }: { results: PollResults }) {
  const maxVotes = Math.max(...results.options.map((o) => o.votes), 1);
  return (
    <div className="w-full h-full flex flex-col px-10 py-12 text-white overflow-y-auto">
      <div className="text-center mb-8">
        <Eyebrow color="#5DCAA5">Poll</Eyebrow>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight max-w-4xl mx-auto">
          {results.question}
        </h1>
        <p className="text-gray-400 text-sm mt-3">
          {results.totalVotes} vote{results.totalVotes !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="max-w-3xl w-full mx-auto space-y-4">
        {results.options.map((opt) => (
          <div key={opt.id}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: opt.color || '#2A7A8A' }}
                />
                <span className="text-lg font-medium">{opt.text}</span>
              </div>
              <span className="text-gray-400 text-sm font-mono">
                {opt.votes} ({opt.percentage}%)
              </span>
            </div>
            <div className="h-9 bg-white/10 rounded-lg overflow-hidden">
              <div
                className="h-full rounded-lg transition-all duration-700 ease-out"
                style={{
                  width: `${(opt.votes / maxVotes) * 100}%`,
                  backgroundColor: opt.color || '#2A7A8A',
                  opacity: 0.85,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NPS                                                                */
/* ------------------------------------------------------------------ */

export function NpsResultsView({
  question,
  results,
}: {
  question: string;
  results: NpsResults;
}) {
  return (
    <Stage>
      <Eyebrow color="#E8A838">Net Promoter Score</Eyebrow>
      <Heading>{question}</Heading>
      <Sub>
        {results.totalResponses} response{results.totalResponses !== 1 ? 's' : ''}
      </Sub>
      {results.totalResponses > 0 ? (
        <div className="w-full max-w-3xl space-y-8">
          <div className="flex h-16 rounded-2xl overflow-hidden text-sm font-bold text-white">
            {results.detractorPct > 0 && (
              <div
                className="bg-[#EF4444] flex items-center justify-center"
                style={{ width: `${results.detractorPct}%` }}
              >
                {results.detractorPct}%
              </div>
            )}
            {results.passivePct > 0 && (
              <div
                className="bg-[#F59E0B] flex items-center justify-center"
                style={{ width: `${results.passivePct}%` }}
              >
                {results.passivePct}%
              </div>
            )}
            {results.promoterPct > 0 && (
              <div
                className="bg-[#10B981] flex items-center justify-center"
                style={{ width: `${results.promoterPct}%` }}
              >
                {results.promoterPct}%
              </div>
            )}
          </div>
          <div className="flex justify-center gap-8 text-sm text-gray-300">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#EF4444]" /> Detractors: {results.detractors}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#F59E0B]" /> Passives: {results.passives}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#10B981]" /> Promoters: {results.promoters}
            </span>
          </div>
          <div className="text-center">
            <div
              className={`text-7xl font-black ${
                results.npsScore > 50
                  ? 'text-[#10B981]'
                  : results.npsScore > 0
                  ? 'text-[#F59E0B]'
                  : 'text-[#EF4444]'
              }`}
            >
              {results.npsScore > 0 ? '+' : ''}
              {results.npsScore}
            </div>
            <p className="text-gray-400 mt-1">Net Promoter Score</p>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">Waiting for responses…</p>
      )}
    </Stage>
  );
}

/* ------------------------------------------------------------------ */
/*  Word cloud                                                         */
/* ------------------------------------------------------------------ */

export function WordCloudResultsView({ results }: { results: WordCloudResults }) {
  const maxCount = Math.max(...results.words.map((w) => w.count), 1);
  // Scale font 18px → 64px by frequency.
  const sizeFor = (count: number) => 18 + Math.round((count / maxCount) * 46);
  const PALETTE = ['#5DCAA5', '#2A7A8A', '#E8A838', '#9FE1CB', '#F59E0B', '#85B7EB'];

  return (
    <Stage>
      <Eyebrow color="#85B7EB">Word Cloud</Eyebrow>
      <Heading>{results.text}</Heading>
      <Sub>
        {results.totalResponses} response{results.totalResponses !== 1 ? 's' : ''} ·{' '}
        {results.totalWords} words
      </Sub>
      {results.words.length > 0 ? (
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 max-w-4xl">
          {results.words.map((w, i) => (
            <span
              key={w.word}
              className="font-bold leading-none"
              style={{ fontSize: sizeFor(w.count), color: PALETTE[i % PALETTE.length] }}
            >
              {w.word}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">Waiting for submissions…</p>
      )}
    </Stage>
  );
}

/* ------------------------------------------------------------------ */
/*  Open question                                                      */
/* ------------------------------------------------------------------ */

export function QuestionResultsView({
  prompt,
  responses,
  summary,
}: {
  prompt: string;
  responses: QuestionResponse[];
  summary?: QuestionSummary | null;
}) {
  // Once an AI summary exists, show it instead of the raw response wall.
  if (summary) {
    return (
      <div className="w-full h-full flex flex-col px-10 py-12 text-white overflow-y-auto">
        <div className="text-center mb-6">
          <Eyebrow color="#7F77DD">AI summary</Eyebrow>
          <h1 className="text-2xl md:text-3xl font-bold leading-tight max-w-4xl mx-auto">
            {prompt}
          </h1>
          <p className="text-gray-400 text-sm mt-2">
            {summary.responseCount} response{summary.responseCount !== 1 ? 's' : ''} · {summary.sentiment}
          </p>
        </div>
        <p className="max-w-3xl w-full mx-auto text-xl leading-relaxed text-center mb-8">
          {summary.oneSentenceSummary}
        </p>
        <div className="max-w-3xl w-full mx-auto space-y-3">
          {summary.themes.map((t, i) => (
            <div key={i} className="bg-white/5 rounded-xl px-5 py-4">
              <h2 className="text-lg font-medium text-white mb-1">{t.theme}</h2>
              <p className="text-sm text-gray-300 leading-snug">{t.description}</p>
              {t.exampleQuotes?.[0] && (
                <p className="text-sm text-gray-400 italic mt-2">“{t.exampleQuotes[0]}”</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col px-10 py-12 text-white overflow-hidden">
      <div className="text-center mb-6">
        <Eyebrow color="#85B7EB">Question</Eyebrow>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight max-w-4xl mx-auto">
          {prompt}
        </h1>
        <p className="text-gray-400 text-sm mt-3">
          {responses.length} response{responses.length !== 1 ? 's' : ''}
        </p>
      </div>
      {responses.length > 0 ? (
        <div className="flex-1 overflow-y-auto max-w-3xl w-full mx-auto grid grid-cols-1 sm:grid-cols-2 gap-3 content-start">
          {responses.slice(0, 40).map((r) => (
            <div key={r._id} className="bg-white/10 rounded-xl px-4 py-3">
              <p className="text-base leading-snug">{r.bodyText}</p>
              {r.participantName && (
                <p className="text-gray-400 text-xs mt-2">— {r.participantName}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center mt-12">Waiting for responses…</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Quiz — current question vote distribution (correct answer is not   */
/*  shown to avoid spoiling before the facilitator reveals it).        */
/* ------------------------------------------------------------------ */

export function QuizResultsView({
  currentIndex,
  revealed,
  correctOptionId,
  results,
}: {
  currentIndex: number;
  revealed: boolean;
  correctOptionId?: string;
  results: QuizResults;
}) {
  const q =
    results.questionResults.find((qr) => qr.questionIndex === currentIndex) ??
    results.questionResults[currentIndex];
  const maxVotes = q ? Math.max(...q.optionResults.map((o) => o.votes), 1) : 1;

  return (
    <div className="w-full h-full flex flex-col px-10 py-12 text-white overflow-y-auto">
      <div className="text-center mb-8">
        <Eyebrow color="#FAC775">Quiz</Eyebrow>
        <p className="text-gray-400 text-sm mb-1">
          {results.title} · Question {currentIndex + 1} of {results.totalQuestions}
        </p>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight max-w-4xl mx-auto">
          {q?.promptText}
        </h1>
        <p className="text-gray-400 text-sm mt-3">
          {q?.totalVotes ?? 0} answer{(q?.totalVotes ?? 0) !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="max-w-3xl w-full mx-auto space-y-4">
        {q?.optionResults.map((opt) => {
          const isCorrect = revealed && correctOptionId && opt.id === correctOptionId;
          const isWrong = revealed && correctOptionId && opt.id !== correctOptionId;
          return (
            <div key={opt.id} style={{ opacity: isWrong ? 0.4 : 1 }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: opt.color || '#2A7A8A' }} />
                  <span className="text-lg font-medium">{opt.text}</span>
                  {isCorrect && (
                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-gray-400 text-sm font-mono">
                  {opt.votes} ({opt.percentage}%)
                </span>
              </div>
              <div className="h-9 bg-white/10 rounded-lg overflow-hidden">
                <div
                  className="h-full rounded-lg transition-all duration-700 ease-out"
                  style={{
                    width: `${(opt.votes / maxVotes) * 100}%`,
                    backgroundColor: isCorrect ? '#10B981' : opt.color || '#2A7A8A',
                    opacity: 0.85,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Quiz leaderboard — standalone full-stage final standings           */
/* ------------------------------------------------------------------ */

export function QuizLeaderboardView({ results }: { results: QuizResults }) {
  const board = results.leaderboard ?? [];
  const medal = ['#FAC775', '#C8D4D8', '#F0997B']; // gold / silver / bronze
  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-10 py-12 text-white">
      <Eyebrow color="#FAC775">Quiz complete</Eyebrow>
      <h1 className="text-3xl md:text-4xl font-bold text-center">{results.title}</h1>
      <p className="text-gray-400 text-sm mt-2 mb-8">Final standings</p>
      {board.length > 0 ? (
        <div className="w-full max-w-lg space-y-2.5">
          {board.slice(0, 8).map((e, i) => (
            <div
              key={`${e.rank}-${e.participantName}`}
              className="flex items-center justify-between rounded-xl px-5 py-3"
              style={{
                backgroundColor: i < 3 ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                border: i < 3 ? `1px solid ${medal[i]}` : '1px solid transparent',
              }}
            >
              <span className="flex items-center gap-4 min-w-0">
                <span
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                  style={{
                    backgroundColor: i < 3 ? medal[i] : 'rgba(255,255,255,0.12)',
                    color: i < 3 ? '#0B1F26' : '#fff',
                  }}
                >
                  {e.rank}
                </span>
                <span className="text-lg font-medium truncate">{e.participantName}</span>
              </span>
              <span className="font-mono text-base text-gray-200 flex-shrink-0">
                {e.score}/{e.total}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">No scores yet.</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SWOT / framework — categorised contributions                      */
/* ------------------------------------------------------------------ */

export function SwotResultsView({
  results,
  summary,
}: {
  results: SwotResults;
  summary?: SwotSummary | null;
}) {
  const categories = Object.keys(results.responseCounts);
  const cols = Math.min(Math.max(categories.length, 1), 4);
  const summarised = !!summary;
  return (
    <div className="w-full h-full flex flex-col px-8 py-10 text-white overflow-hidden">
      <div className="text-center mb-5">
        <Eyebrow color={summarised ? '#7F77DD' : '#AFA9EC'}>
          {results.frameworkType || 'SWOT'}{summarised ? ' · AI summary' : ''}
        </Eyebrow>
        <h1 className="text-2xl md:text-3xl font-bold">{results.title}</h1>
        <p className="text-gray-400 text-sm mt-2">{results.totalResponses} contributions</p>
      </div>
      <div
        className="flex-1 min-h-0 grid gap-3 max-w-5xl w-full mx-auto"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {categories.map((cat) => {
          const points = summary?.categorySummaries?.[cat];
          return (
            <div key={cat} className="bg-white/5 rounded-xl p-3 overflow-y-auto">
              <h2 className="text-sm font-bold text-white/90 mb-2">
                {cat}{' '}
                <span className="text-gray-400 font-normal">({results.responseCounts[cat] || 0})</span>
              </h2>
              <div className="space-y-2">
                {summarised
                  ? (points || []).map((p, i) => (
                      <div key={i} className="bg-white/10 rounded-lg px-3 py-2 text-sm leading-snug">
                        {p.point}
                        {p.count > 1 && <span className="text-gray-400"> ×{p.count}</span>}
                      </div>
                    ))
                  : (results.categories[cat] || []).slice(0, 15).map((e) => (
                      <div key={e._id} className="bg-white/10 rounded-lg px-3 py-2 text-sm leading-snug">
                        {e.text}
                      </div>
                    ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Cross-tab — one 100% stacked bar per Q1 option, split by Q2        */
/* ------------------------------------------------------------------ */

export function CrossTabResultsView({ results }: { results: CrossTabResults }) {
  const q2 = results.question2.options;
  return (
    <div className="w-full h-full flex flex-col px-10 py-12 text-white overflow-y-auto">
      <div className="text-center mb-6">
        <Eyebrow color="#F0997B">Cross-tab</Eyebrow>
        <h1 className="text-2xl md:text-3xl font-bold max-w-4xl mx-auto">{results.title}</h1>
        <p className="text-gray-400 text-sm mt-2">{results.totalResponses} responses</p>
      </div>
      <div className="max-w-3xl w-full mx-auto space-y-4">
        {results.question1.options.map((o1) => {
          const row = results.matrix[o1.id] || {};
          const rowTotal = q2.reduce((s, o2) => s + (row[o2.id] || 0), 0);
          const denom = rowTotal || 1;
          return (
            <div key={o1.id}>
              <div className="flex justify-between mb-1">
                <span className="text-base font-medium">{o1.text}</span>
                <span className="text-gray-400 text-sm">{rowTotal}</span>
              </div>
              <div className="flex h-8 rounded-lg overflow-hidden bg-white/10">
                {q2.map((o2) => {
                  const c = row[o2.id] || 0;
                  if (c === 0) return null;
                  const pct = c / denom;
                  return (
                    <div
                      key={o2.id}
                      className="flex items-center justify-center text-xs font-medium text-white"
                      style={{ width: `${pct * 100}%`, backgroundColor: o2.color, opacity: 0.85 }}
                      title={`${o2.text}: ${c}`}
                    >
                      {pct >= 0.1 ? c : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap justify-center gap-4 mt-6 text-sm text-gray-300">
        {q2.map((o2) => (
          <span key={o2.id} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: o2.color }} />
            {o2.text}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Exam — self-paced; results are facilitator-only, so the shared     */
/*  stage shows a status card (mirrors the web ExamDisplay).           */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Slide — full-bleed slide image (public endpoint, no token)         */
/* ------------------------------------------------------------------ */

export function SlideView({ slideId, title }: { slideId: string; title: string }) {
  // Load via blob fetch (like image activities) — more reliable than a
  // cross-origin <img> in the Zoom webview, and surfaces the real status.
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setObjUrl(null);
    setErr(null);
    fetchSlideImageObjectUrl(slideId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjUrl(url);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('Slide image failed to load:', slideId, e);
          setErr(e instanceof Error ? e.message : 'error');
        }
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [slideId]);

  if (err) {
    return (
      <Stage>
        <Eyebrow color="#C8D4D8">Slide</Eyebrow>
        <Heading>{title}</Heading>
        <Sub>No slide image available{err ? ` (${err})` : ''}</Sub>
      </Stage>
    );
  }
  if (!objUrl) {
    return (
      <Stage>
        <Eyebrow color="#C8D4D8">Slide</Eyebrow>
        <Heading>{title}</Heading>
        <div className="mt-2 h-8 w-8 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
      </Stage>
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <img src={objUrl} alt={title} className="max-w-full max-h-full object-contain rounded-lg" />
    </div>
  );
}

export function ExamView({ title, totalQuestions }: { title: string; totalQuestions: number }) {
  return (
    <Stage>
      <Eyebrow color="#F09595">Exam</Eyebrow>
      <Heading>{title}</Heading>
      <Sub>Participants are completing this exam at their own pace on their devices.</Sub>
      <p className="text-gray-400 text-sm">
        {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}
      </p>
    </Stage>
  );
}

/* ------------------------------------------------------------------ */
/*  Image activity — image with normalized-coordinate response markers */
/*  (click dots or drag-drop labels). Image rides an authorized URL.   */
/* ------------------------------------------------------------------ */

export function ImageActivityResultsView({ results }: { results: ImageActivityResults }) {
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState<string | null>(null);
  const imagePath = results.imagePath;

  useEffect(() => {
    if (!imagePath) return;
    let cancelled = false;
    let created: string | null = null;
    setObjUrl(null);
    setImgError(null);
    fetchUploadObjectUrl(imagePath)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setObjUrl(url);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('Image activity image failed to load:', imagePath, e);
          setImgError(e instanceof Error ? e.message : 'error');
        }
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [imagePath]);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center px-8 py-10 text-white">
      <div className="text-center mb-4">
        <Eyebrow color="#85B7EB">Image activity</Eyebrow>
        <Heading>{results.title}</Heading>
        <Sub>
          {results.totalResponses} response{results.totalResponses !== 1 ? 's' : ''}
        </Sub>
      </div>
      {imagePath && objUrl ? (
        <div className="relative inline-block max-w-full">
          <img
            src={objUrl}
            alt={results.title}
            className="block max-h-[60vh] max-w-full rounded-lg"
          />
          {results.responses.map((r) => {
            if (results.mode === 'click' && r.clickX != null && r.clickY != null) {
              return (
                <span
                  key={r._id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-[#E8A838] ring-2 ring-white/80"
                  style={{ left: `${r.clickX * 100}%`, top: `${r.clickY * 100}%` }}
                  title={r.participantName}
                />
              );
            }
            if (results.mode === 'dragdrop' && r.placements) {
              return r.placements.map((p, pi) => (
                <span
                  key={`${r._id}-${pi}`}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-md bg-[#2A7A8A] text-white text-xs font-medium whitespace-nowrap shadow"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                >
                  {p.itemLabel}
                </span>
              ));
            }
            return null;
          })}
        </div>
      ) : imgError ? (
        <p className="text-gray-400 text-sm max-w-md text-center">
          Couldn't load the activity image{imgError ? ` (${imgError})` : ''}.
          {imgError === '404' && ' The image file is missing — try re-uploading it.'}
        </p>
      ) : (
        <p className="text-gray-500">Loading image…</p>
      )}
    </div>
  );
}
